import React from 'react'
import {
    Button,
    Flex,
    IconChevronRightSmall,
    phoneFormatter,
    Spinner,
    Tag,
} from '@dostavkee/web-ui'
import { useNavigate } from '@tanstack/react-router'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { useInView } from 'react-intersection-observer'
import { toast } from 'sonner'

import { useAuthCityValue } from '@/entities/auth'
import { useRecruitmentFetchInfinite } from '@/entities/recruitment'

import {
    PAGE_SIZE,
    RECRUITMENT_STATUS_TO_TAG_VARIANT,
    RECRUITMENT_STATUS_TO_TEXT,
} from '../../model/constants'
import { getDriverFullName } from '../../model/get-driver-full-name'
import { getRecruitmentStatus } from '../../model/get-recruitment-status'
import { useRecruitmentSearch } from '../../model/use-recruitment-search'
import { useRecruitmentTotal } from '../../model/use-recruitment-total'
import { RecruitmentConnectionStages } from '../recruitment-connection-stages/recruitment-connection-stages'
import styles from './recruitment-cards.module.scss'

export const RecruitmentCards = () => {
    const {
        isSearch,
        searchValues: { page, ...searchValues },
    } = useRecruitmentSearch()

    const { selectedCity } = useAuthCityValue()
    const { setRecruitmentTotal } = useRecruitmentTotal()

    const { recruitment, total, fetchNextPage, isLoading, isFetchingNextPage, hasNextPage } =
        useRecruitmentFetchInfinite({
            params: {
                ...searchValues,
                limit: PAGE_SIZE,
                offset: (page - 1) * PAGE_SIZE,
                city_id: selectedCity?.id,
            },
        })

    const navigate = useNavigate({
        from: '/recruitment',
    })

    const isEmpty = recruitment?.[0] === null || !recruitment?.length

    React.useEffect(() => {
        if (!isLoading && typeof total === 'number') {
            setRecruitmentTotal(total)
        }
    }, [total, setRecruitmentTotal, isLoading])

    const { ref } = useInView({
        rootMargin: '20px',
        onChange: (inView) => {
            if (inView && hasNextPage) {
                fetchNextPage()
            }
        },
    })

    if (isLoading) {
        return (
            <div
                className={clsx({
                    [styles['recruitment-cards']]: true,
                    [styles['recruitment-cards--state']]: true,
                    [styles['recruitment-cards--state--searched']]: isSearch,
                })}
            >
                <Spinner />
            </div>
        )
    }

    if (!isLoading && isEmpty) {
        return (
            <div
                className={clsx({
                    [styles['recruitment-cards']]: true,
                    [styles['recruitment-cards--state']]: true,
                    [styles['recruitment-cards--state--searched']]: isSearch,
                })}
            >
                {isSearch ? (
                    <div className={styles['recruitment-cards__empty-container']}>
                        <div className={styles['recruitment-cards__empty-title']}>
                            Ничего не нашлось
                        </div>
                        <div className={styles['recruitment-cards__empty-description']}>
                            Попробуйте изменить параметры поиска
                        </div>
                    </div>
                ) : (
                    <div className={styles['recruitment-cards__empty-container']}>
                        <div className={styles['recruitment-cards__empty-title']}>
                            Список вербовок пуст
                        </div>
                    </div>
                )}
            </div>
        )
    }

    return (
        <div
            className={styles['recruitment-cards']}
            style={{
                paddingTop: isSearch ? 54 : 0,
            }}
        >
            {recruitment?.map((item) => {
                const request = item.user_request
                const recruitmentStatus = getRecruitmentStatus(item.status, request?.state)
                const fullName = getDriverFullName(request)

                return (
                    <section
                        key={item.id}
                        className={styles['recruitment-card']}
                        onClick={() => {
                            if (request?.is_active) {
                                navigate({
                                    to: '/requests/$id',
                                    params: { id: request.public_id.toString() },
                                })
                            } else {
                                toast.warning(
                                    <div>
                                        <p>Редактирование недоступно.</p>
                                        <p>Анкета создана ранее другим партнёром</p>
                                    </div>
                                )
                            }
                        }}
                    >
                        <header className={styles['recruitment-card__header']}>
                            <div className={styles['recruitment-card__header-prefix']}>
                                {request ? (
                                    <>
                                        <p>№ {request.public_id}</p>
                                        <p>
                                            {dayjs(request.created_at).format('HH:mm, DD.MM.YYYY')}
                                        </p>
                                    </>
                                ) : (
                                    <div>Анкета создана ранее другим партнёром</div>
                                )}
                            </div>
                            <div className={styles['recruitment-card__header-postfix']}>
                                <Tag
                                    label={RECRUITMENT_STATUS_TO_TEXT[recruitmentStatus]}
                                    size='sm'
                                    variant={RECRUITMENT_STATUS_TO_TAG_VARIANT[recruitmentStatus]}
                                />
                                <Button
                                    isText
                                    disabled={!request?.is_active}
                                    variant='ghost'
                                    onClick={() => {
                                        if (request?.is_active) {
                                            navigate({
                                                to: '/requests/$id',
                                                params: { id: request.public_id.toString() },
                                            })
                                        }
                                    }}
                                >
                                    <IconChevronRightSmall
                                        color='var(--text-and-icon-secondary)'
                                        size={24}
                                    />
                                </Button>
                            </div>
                        </header>
                        <div>
                            <div className={styles['recruitment-card__label']}>Водитель</div>
                            <div
                                className={clsx(
                                    styles['recruitment-card__value'],
                                    styles['recruitment-card__value--fullname']
                                )}
                            >
                                {fullName}
                            </div>
                            <div className={styles['recruitment-card__value']}>
                                {phoneFormatter(request?.phone ?? String(item.phone))}
                            </div>
                        </div>
                        <div>
                            <div className={styles['recruitment-card__label']}>
                                Статус подключения
                            </div>
                            <RecruitmentConnectionStages recruitment={item} />
                        </div>
                    </section>
                )
            })}

            {isFetchingNextPage && (
                <Flex align='center' justify='center' style={{ position: 'relative' }}>
                    <Spinner />
                </Flex>
            )}
            <div ref={ref} />
        </div>
    )
}
